import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { Plus, Check, X, Loader2 } from 'lucide-react';
import { api } from '../services/api';
import { IOS_SPRING } from '../constants';

// ── Memory Cards — V8: loads structured categories from PostgreSQL ──
// Used standalone AND embedded in SettingsView profile page.
// Three canonical categories: identity, preferences, lifestyle.

const CANONICAL_SECTIONS = [
  { name: 'identity', title: 'About You' },
  { name: 'preferences', title: 'Preferences' },
  { name: 'lifestyle', title: 'Lifestyle' },
];

export default function MemoryContent({ livekit, memoryUpdatedAt: externalUpdate }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load structured memory from PostgreSQL via API
  const loadMemory = useCallback(async () => {
    try {
      const data = await api.getStructuredMemory();
      const catMap = {};
      for (const cat of data?.categories || []) {
        catMap[cat.name] = cat.items || [];
      }
      // Always show all 3 canonical sections, merging legacy category names
      const legacyMap = { entities: 'identity', events: 'lifestyle', relations: 'identity', skills: 'identity' };
      for (const [legacy, canonical] of Object.entries(legacyMap)) {
        if (catMap[legacy]) {
          catMap[canonical] = [...(catMap[canonical] || []), ...catMap[legacy]];
          delete catMap[legacy];
        }
      }
      setSections(
        CANONICAL_SECTIONS.map(cs => ({
          title: cs.title,
          name: cs.name,
          items: catMap[cs.name] || [],
        }))
      );
    } catch (e) {
      console.error('Failed to load structured memory:', e);
      setSections(CANONICAL_SECTIONS.map(cs => ({ ...cs, items: [] })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadMemory();
  }, [loadMemory]);

  // Reload when livekit DataChannel or SSE signals memory update
  useEffect(() => {
    if (!livekit?.memoryUpdatedAt) return;
    loadMemory();
  }, [livekit?.memoryUpdatedAt, loadMemory]);

  useEffect(() => {
    if (!externalUpdate) return;
    loadMemory();
  }, [externalUpdate, loadMemory]);

  // ── Item operations (V8 fact-based API) ──
  const handleEditItem = useCallback(async (sectionIdx, itemIdx, newContent) => {
    setSaving(true);
    try {
      const section = sections[sectionIdx];
      const item = section.items[itemIdx];
      // Delete old fact + add new one (atomic edit via two API calls)
      if (item.id) await api.deleteFact(item.id);
      const result = await api.addFact(section.name, newContent);
      const newId = result?.fact_id || item.id;
      setSections(prev => prev.map((s, si) => si === sectionIdx
        ? { ...s, items: s.items.map((it, ii) => ii === itemIdx ? { ...it, id: newId, content: newContent } : it) }
        : s
      ));
    } catch (e) {
      console.error('Failed to edit memory item:', e);
    } finally {
      setSaving(false);
    }
  }, [sections]);

  const handleDeleteItem = useCallback(async (sectionIdx, itemIdx) => {
    setSaving(true);
    try {
      const item = sections[sectionIdx].items[itemIdx];
      if (item.id) await api.deleteFact(item.id);
      setSections(prev => prev.map((s, si) => si === sectionIdx
        ? { ...s, items: s.items.filter((_, ii) => ii !== itemIdx) }
        : s
      ));
    } catch (e) {
      console.error('Failed to delete memory item:', e);
    } finally {
      setSaving(false);
    }
  }, [sections]);

  const handleAddItem = useCallback(async (sectionIdx, content) => {
    setSaving(true);
    try {
      const section = sections[sectionIdx];
      const result = await api.addFact(section.name, content);
      const newId = result?.fact_id || crypto.randomUUID();
      setSections(prev => prev.map((s, si) => si === sectionIdx
        ? { ...s, items: [...s.items, { id: newId, content }] }
        : s
      ));
    } catch (e) {
      console.error('Failed to add memory item:', e);
    } finally {
      setSaving(false);
    }
  }, [sections]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin" style={{ color: 'rgba(0,0,0,0.15)' }} />
      </div>
    );
  }

  // ── Card View (always shows 3 canonical sections) ──
  return (
    <div className="space-y-3">
      {saving && (
        <div className="flex items-center justify-center gap-2 py-1.5">
          <Loader2 size={12} className="animate-spin" style={{ color: 'rgba(0,0,0,0.3)' }} />
          <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)' }}>Saving...</span>
        </div>
      )}
      {sections.map((section, sectionIdx) => (
        <SectionCard
          key={section.name || section.title}
          section={section}
          sectionIdx={sectionIdx}
          onEditItem={handleEditItem}
          onDeleteItem={handleDeleteItem}
          onAddItem={handleAddItem}
        />
      ))}
    </div>
  );
}

// ── Section Card ──
function SectionCard({ section, sectionIdx, onEditItem, onDeleteItem, onAddItem }) {
  const [adding, setAdding] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: sectionIdx * 0.04, ...IOS_SPRING }}
      style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      {/* Section header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <span className="font-semibold" style={{ fontSize: 15, color: '#000' }}>
          {section.title}
        </span>
        <button
          onClick={() => setAdding(true)}
          className="p-1 rounded-full hover:bg-black/[0.04] transition-colors"
          style={{ color: 'rgba(0,0,0,0.3)' }}
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Items */}
      <div className="px-4 pb-3">
        {section.items.length === 0 && !adding && (
          <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.2)', padding: '4px 0' }}>
            Tap + to add
          </p>
        )}
        <AnimatePresence initial={false}>
          {section.items.map((item, itemIdx) => (
            <MemoryItem
              key={item.id || `${item.content}-${itemIdx}`}
              value={item.content}
              source={item.source}
              onEdit={(val) => onEditItem(sectionIdx, itemIdx, val)}
              onDelete={() => onDeleteItem(sectionIdx, itemIdx)}
            />
          ))}
        </AnimatePresence>

        {/* Add new item inline */}
        <AnimatePresence>
          {adding && (
            <AddItemInput
              onSubmit={(val) => {
                onAddItem(sectionIdx, val);
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ── Single Memory Item (swipe-to-delete + tap-to-edit) ──
const DELETE_ZONE_WIDTH = 72;
const SWIPE_THRESHOLD = 40;

function MemoryItem({ value, source, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [showDelete, setShowDelete] = useState(false);
  const inputRef = useRef(null);
  const dragX = useMotionValue(0);
  const deleteOpacity = useTransform(dragX, [-DELETE_ZONE_WIDTH, -SWIPE_THRESHOLD, 0], [1, 0.5, 0]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) {
      onEdit(trimmed);
    }
    setEditing(false);
  };

  const handleDragEnd = (_, info) => {
    if (info.offset.x < -SWIPE_THRESHOLD) {
      animate(dragX, -DELETE_ZONE_WIDTH, { type: 'spring', stiffness: 400, damping: 35 });
      setShowDelete(true);
    } else {
      animate(dragX, 0, { type: 'spring', stiffness: 400, damping: 35 });
      setShowDelete(false);
    }
  };

  const handleDeleteConfirm = () => {
    onDelete();
    setShowDelete(false);
  };

  const handleCloseSwipe = () => {
    animate(dragX, 0, { type: 'spring', stiffness: 400, damping: 35 });
    setShowDelete(false);
  };

  if (editing) {
    return (
      <motion.div
        layout
        className="flex items-center gap-2 py-1.5"
        style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}
      >
        <input
          ref={inputRef}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.target.blur(); }
            if (e.key === 'Escape') { setEditValue(value); setEditing(false); }
          }}
          className="flex-1 px-2 py-1 rounded-lg focus:outline-none"
          style={{
            fontSize: 14, color: '#000', background: 'rgba(0,0,0,0.03)',
            border: '1px solid rgba(0,122,255,0.3)',
          }}
        />
        <button
          onMouseDown={e => e.preventDefault()}
          onClick={handleSave}
          className="shrink-0 px-2 py-0.5 rounded-md active:opacity-70 transition-opacity"
          style={{ color: 'rgb(0,122,255)', fontSize: 14, fontWeight: 500 }}
        >
          Done
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
      transition={IOS_SPRING}
      style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(0,0,0,0.04)' }}
    >
      {/* Delete zone (behind content) */}
      <motion.div
        style={{
          position: 'absolute', right: 0, top: 0, bottom: 0,
          width: DELETE_ZONE_WIDTH,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          background: 'rgba(255,59,48,0.9)',
          borderRadius: '0 0 0 0',
          opacity: deleteOpacity,
        }}
      >
        <button
          onClick={handleDeleteConfirm}
          className="flex items-center justify-center w-full h-full"
          style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}
        >
          Delete
        </button>
      </motion.div>

      {/* Swipeable content row */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: -DELETE_ZONE_WIDTH, right: 0 }}
        dragElastic={0.1}
        onDragEnd={handleDragEnd}
        style={{ x: dragX, background: '#fff', position: 'relative', zIndex: 1 }}
        className="flex items-center gap-2 py-1.5"
        onClick={() => {
          if (!showDelete) {
            setEditValue(value);
            setEditing(true);
          } else {
            handleCloseSwipe();
          }
        }}
      >
        <span className="shrink-0" style={{ color: 'rgba(0,0,0,0.2)', fontSize: 10 }}>●</span>
        <span
          className="flex-1 cursor-pointer select-none"
          style={{ fontSize: 14, color: 'rgba(0,0,0,0.75)', lineHeight: 1.5 }}
        >
          {value}
        </span>
        {source === 'implicit' && (
          <span className="shrink-0 px-1.5 py-0.5 rounded-full" style={{
            fontSize: 10, fontWeight: 500, color: 'rgb(88,86,214)',
            background: 'rgba(88,86,214,0.08)', letterSpacing: '0.02em',
          }}>inferred</span>
        )}
        {source === 'pattern' && (
          <span className="shrink-0 px-1.5 py-0.5 rounded-full" style={{
            fontSize: 10, fontWeight: 500, color: 'rgb(34,197,94)',
            background: 'rgba(34,197,94,0.08)', letterSpacing: '0.02em',
          }}>pattern</span>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── Add Item Input ──
function AddItemInput({ onSubmit, onCancel }) {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (trimmed) onSubmit(trimmed);
    else onCancel();
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={IOS_SPRING}
      className="flex items-center gap-2 pt-2"
    >
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Add new item..."
        className="flex-1 px-2 py-1 rounded-lg focus:outline-none"
        style={{
          fontSize: 14, color: '#000', background: 'rgba(0,0,0,0.03)',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      />
      <button onClick={handleSubmit}
        className="p-1 rounded-full hover:bg-black/[0.04] transition-colors"
        style={{ color: 'rgba(52,199,89,0.8)' }}>
        <Check size={14} />
      </button>
      <button onClick={onCancel}
        className="p-1 rounded-full hover:bg-black/[0.04] transition-colors"
        style={{ color: 'rgba(0,0,0,0.3)' }}>
        <X size={14} />
      </button>
    </motion.div>
  );
}
